import {useFlags} from 'launchdarkly-react-client-sdk';
import React from 'react';
import {Label, designSystemToken} from '@lightricks/react-design-system';
import translate from '@/utils/translate';
import useDashboardUserQuery from '@/hooks/queries/use-dashboard-user-query';

type OffPlatformBadgeProps = {
  variant?: 'label' | 'icon';
  className?: string;
};

function OffPlatformInfoGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle
        cx="12"
        cy="12"
        r="9"
        fill={designSystemToken('semantic.bg.white')}
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M12 11V17"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M11.75 8V7H12.25V8H11.75Z"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function OffPlatformBadge({
  variant = 'label',
  className,
}: OffPlatformBadgeProps) {
  const flags = useFlags();
  const {dashboardUser} = useDashboardUserQuery({});
  const visible =
    Boolean(flags.offPlatformCrmBadge) &&
    Boolean(dashboardUser?.hasPlatformAdminRole);

  if (!visible) {
    return null;
  }

  const label = translate('Off-platform');

  if (variant === 'icon') {
    return (
      <span
        className={className}
        data-testid="off-platform-avatar-mark"
        title={label}
        role="img"
        aria-label={label}
      >
        <OffPlatformInfoGlyph />
      </span>
    );
  }

  return (
    <Label
      size="sm"
      data-testid="off-platform-badge"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        height: 20,
        paddingInline: '6px',
        borderRadius: '6px',
        backgroundColor: designSystemToken('semantic.bg.tertiary'),
        color: designSystemToken('semantic.fg.secondary'),
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </Label>
  );
}
